import { setPageMeta } from '../seo'
import React, { useEffect, useState } from 'react'
import SiteFooter from '../components/SiteFooter'

// The diary reads /assets/hlo/diary.json rather than a file inside src, so an
// entry can be added without rebuilding and redeploying the site. Whatever way
// the entries get written — by hand in cPanel, from a Google Sheet, or from a
// small form on a phone — this page does not change.
//
// The cost of that choice: content fetched by JavaScript is indexed less
// reliably than content in the build. A diary's job is the audience rather than
// search, so it is the right trade here, but it is a real one.

const DiaryEntry = ({ entry }) => (
  <li className='diary-entry'>
    <span className='diary-date'>{entry.date}</span>
    <h2>{entry.title}</h2>
    {entry.images && entry.images.length > 0 && (
      <div className={`diary-images diary-images--${entry.images.length === 1 ? 'one' : 'many'}`}>
        {entry.images.map((img) => (
          <figure key={img.src}>
            <img src={img.src} alt={img.alt || ''} loading='lazy' />
            {img.caption && <figcaption>{img.caption}</figcaption>}
          </figure>
        ))}
      </div>
    )}
    {(entry.body || []).map((para) => (
      <p key={para.slice(0, 48)}>{para}</p>
    ))}
    {entry.place && <p className='diary-place'>{entry.place}</p>}
  </li>
)

const ProducersDiary = () => {
  const [entries, setEntries] = useState(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    setPageMeta({
      title: 'Producer\u2019s Diary | Hands Like Ours',
      path: '/handslikeours/producers-diary',
      description: 'Notes from the making of Hands Like Ours, a short documentary in North Sumatra. Written on the road by Elaine Lim.',
    })
    window.scrollTo(0, 0)
  }, [])

  useEffect(() => {
    let live = true
    // cache-busted, or a new entry sits behind the old file for hours
    fetch(`/assets/hlo/diary.json?v=${Date.now()}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((data) => {
        if (!live) return
        const list = Array.isArray(data) ? data : data.entries || []
        setEntries(list)
      })
      .catch(() => live && setFailed(true))
    return () => { live = false }
  }, [])

  return (
    <div className='App page-standard page-hlo page-diary'>
      <header className='diary-header'>
        <div className='content-container'>
          <a className='diary-back' href='/handslikeours'>Hands Like Ours</a>
          <img
            className='diary-mark'
            src='/assets/hlo/hands-like-ours-mark.png'
            alt=''
            aria-hidden='true'
            width='560'
            height='560'
          />
          <h1>Producer&rsquo;s Diary</h1>
          <p className='diary-sub'>
            Notes from the making of the film, written on the road.
          </p>
        </div>
      </header>

      <main>
        <section data-tone='dark'>
          <div className='content-container'>
            {entries === null && !failed && (
              <p className='diary-empty'>Loading&hellip;</p>
            )}

            {(failed || (entries && entries.length === 0)) && (
              <p className='diary-empty'>
                The first entries will be written on the recce in North Sumatra.
                Follow along on{' '}
                <a href='https://www.instagram.com/thegirlandherpiano' target='_blank' rel='noopener noreferrer'>Instagram</a>
                {' '}in the meantime.
              </p>
            )}

            {entries && entries.length > 0 && (
              <ul className='diary-list'>
                {entries.map((entry) => (
                  <DiaryEntry key={entry.date + entry.title} entry={entry} />
                ))}
              </ul>
            )}
          </div>
        </section>
      </main>

      <SiteFooter
        mark='/assets/hlo/hands-like-ours-mark.png'
        markHover='/assets/hlo/hands-like-ours-mark.png'
      />
    </div>
  )
}

export default ProducersDiary
